import { ANALYSIS_FPS, ANALYSIS_WIDTH } from '../config.js';
import { rgbaToGray, type GrayFrame } from './metrics.js';

/**
 * Analysis loop — §8: recording runs at full 30 FPS; analysis samples ~10 FPS from the
 * live <video> element into a small offscreen canvas so the encoder is never blocked.
 *
 * Frame timing uses a MONOTONIC WALL CLOCK (performance.now, ms since the loop started),
 * NOT the video's `mediaTime`. `requestVideoFrameCallback().mediaTime` is unreliable for
 * live camera MediaStreams on some platforms (notably iOS Safari, where it can stay at
 * 0) — driving durations/timers off it froze the whole recording + liveness state
 * machine on real devices. Wall-clock elapsed always advances; it is what the user
 * experiences, matches the recorder's own start moment for §19 hints (±300 ms window),
 * and the backend re-measures true PTS duration/FPS independently (§20/§22).
 */

export interface AnalyzedFrame {
  gray: GrayFrame;
  /** Monotonic ms since the analysis loop started (wall clock, always advances). */
  mediaTimeMs: number;
}

type VideoWithRvfc = HTMLVideoElement & {
  requestVideoFrameCallback?: (cb: (now: number, meta: { mediaTime: number }) => void) => number;
  cancelVideoFrameCallback?: (id: number) => void;
};

export class AnalysisLoop {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private running = false;
  private startedAt = 0;
  private lastSampleAt = 0;
  private rvfcId: number | null = null;
  private intervalId: number | null = null;

  constructor(
    private video: HTMLVideoElement,
    private onFrame: (frame: AnalyzedFrame) => void,
  ) {
    this.canvas = document.createElement('canvas');
    const ctx = this.canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) throw new Error('2D canvas unavailable');
    this.ctx = ctx;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.startedAt = performance.now();
    const v = this.video as VideoWithRvfc;
    if (typeof v.requestVideoFrameCallback === 'function') {
      // rVFC's `now` arg is a performance.now()-style timestamp — used for cadence AND
      // as the monotonic frame time (mediaTime is deliberately ignored, see class doc).
      const tick = (now: number) => {
        if (!this.running) return;
        if (now - this.lastSampleAt >= 1000 / ANALYSIS_FPS) {
          this.lastSampleAt = now;
          this.sample(now - this.startedAt);
        }
        this.rvfcId = v.requestVideoFrameCallback!(tick);
      };
      this.rvfcId = v.requestVideoFrameCallback(tick);
    } else {
      this.intervalId = window.setInterval(() => {
        if (!this.running) return;
        this.sample(performance.now() - this.startedAt);
      }, 1000 / ANALYSIS_FPS);
    }
  }

  stop(): void {
    this.running = false;
    const v = this.video as VideoWithRvfc;
    if (this.rvfcId !== null && v.cancelVideoFrameCallback) v.cancelVideoFrameCallback(this.rvfcId);
    this.rvfcId = null;
    if (this.intervalId !== null) window.clearInterval(this.intervalId);
    this.intervalId = null;
  }

  private sample(mediaTimeMs: number): void {
    const vw = this.video.videoWidth;
    const vh = this.video.videoHeight;
    if (!vw || !vh) return;
    const w = ANALYSIS_WIDTH;
    const h = Math.round((vh / vw) * w);
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
    // §7 — analysis works on unmirrored canonical frames; any selfie mirroring is
    // CSS-only on the preview element.
    this.ctx.drawImage(this.video, 0, 0, w, h);
    const rgba = this.ctx.getImageData(0, 0, w, h);
    this.onFrame({ gray: rgbaToGray(rgba.data, w, h), mediaTimeMs });
  }
}

export interface PreflightResult {
  /** Delivered frame rate measured over wall-clock time, after warm-up. */
  averageFps: number;
  frameCount: number;
  /** True only when we collected a confident sample; false → let the backend decide. */
  measured: boolean;
}

/**
 * §13 — camera FPS preflight. Measured over WALL-CLOCK time via rVFC's timestamp (the
 * true delivered rate), and critically only AFTER a warm-up window — front/rear cameras
 * ramp up auto-exposure/focus for the first ~1s and under-deliver frames during that
 * period, which would otherwise fail even a flagship phone. This is advisory: the
 * authoritative FPS SLA is recomputed server-side on real timestamps (§22), so a low or
 * unmeasurable reading never hard-blocks capture on the low-assurance web path.
 */
export async function preflightFps(
  video: HTMLVideoElement,
  opts: { warmupMs?: number; measureMs?: number } = {},
): Promise<PreflightResult> {
  const warmupMs = opts.warmupMs ?? 900;
  const measureMs = opts.measureMs ?? 2200;
  const v = video as VideoWithRvfc;
  if (typeof v.requestVideoFrameCallback !== 'function') {
    return { averageFps: 0, frameCount: 0, measured: false };
  }

  const nows: number[] = [];
  return await new Promise<PreflightResult>((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      if (nows.length < 12) return resolve({ averageFps: 0, frameCount: nows.length, measured: false });
      const span = nows[nows.length - 1]! - nows[0]!;
      const averageFps = span > 0 ? ((nows.length - 1) / span) * 1000 : 0;
      resolve({ averageFps: Number(averageFps.toFixed(1)), frameCount: nows.length, measured: true });
    };
    const start = performance.now();
    const tick = (now: number) => {
      const elapsed = now - start;
      if (elapsed >= warmupMs) nows.push(now); // ignore warm-up frames
      if (elapsed >= warmupMs + measureMs) return finish();
      v.requestVideoFrameCallback!(tick);
    };
    v.requestVideoFrameCallback!(tick);
    // Wall-clock backstop: rVFC only fires on presented frames, so if the camera stalls
    // during the window the chain would never resolve and capture would never start.
    setTimeout(finish, warmupMs + measureMs + 2_000);
  });
}
